import { PaymentMethod } from '@orderna/admin-panel/src/app/model/enum/payment-method';
import { TransactionStatus } from '@orderna/admin-panel/src/app/model/enum/transaction-status';
import { Customer } from '@orderna/admin-panel/src/app/model/customer';
import { Store } from './store';
import { NIL as NIL_UUID } from 'uuid';
import { User } from './user';
import { TransactionType } from './enum/transaction-type';
import {
  TransactionItem,
  TransactionItemCreateData,
  TransactionItemUpdateData,
} from './transaction-item';
import { DiscountDetail } from './discount';
import { CashRegister } from './cash-register';

export interface ITransactionDerivedValues {
  salePrice: number;
  refundAmount: number;
  discountAmount: number;
  netSales: number;
  taxAmount: number;
  serviceAmount: number;
  grossSales: number;
}

export interface ITransaction extends ITransactionDerivedValues {
  id: string;
  uuid: string;
  transactionTimestamp: Date;
  item: TransactionItem[];
  staff: User;
  customer: Customer | null;
  discount: DiscountDetail | null;
  transactionLevelDiscount: number;
  salesTaxRate: number;
  serviceChargeRate: number | null;
  status: TransactionStatus;
  totalItems: number;
  store: Store;
  registerId: CashRegister['id'];
  register: { name: string };
  paymentMethod: PaymentMethod;
  note: string;
  reason?: string;
  type: TransactionType;
  createdAt: Date;
  updatedAt: Date;
  refundReason?: boolean;
  isRefund?: boolean;
  // analytics
  finalNetAmount: number;
  salesTaxAmount: number;
  serviceChargeAmount: number;
  tip: number | null;
  totalRefund: number;
  grossAmount: number;
  costOfGoods: number;
  transactionItem?:any;
  totalDiscount?: any;
}

export type TransactionCreateData = Pick<
  ITransaction,
  | 'customer'
  | 'store'
  | 'registerId'
  | 'staff'
  | 'discount'
  | 'serviceChargeRate'
  | 'tip'
  | 'salesTaxRate'
  | 'paymentMethod'
  | 'status'
  | 'type'
  | 'transactionTimestamp'
  | 'note'
  | 'reason'
  | 'refundReason'
  | 'isRefund'
> & { item: TransactionItemCreateData[] };

export type TransactionUpdateData = Pick<ITransaction, 'id'> &
  Partial<
    Pick<
      ITransaction,
      | 'customer'
      | 'store'
      | 'registerId'
      | 'staff'
      | 'discount'
      | 'serviceChargeRate'
      | 'tip'
      | 'salesTaxRate'
      | 'paymentMethod'
      | 'status'
      | 'type'
      | 'transactionTimestamp'
      | 'note'
      | 'reason'
      | 'refundReason'
      | 'isRefund'
    >
  > & { item?: TransactionItemUpdateData[] };

export class Transaction implements ITransaction {
  constructor(
    public readonly id: ITransaction['id'] = NIL_UUID,
    public readonly transactionTimestamp: ITransaction['transactionTimestamp'] = new Date(),
    public readonly item: ITransaction['item'] = [],
    public readonly staff: ITransaction['staff'] = new User(),
    public readonly customer: ITransaction['customer'] = new Customer(),
    public readonly finalNetAmount: ITransaction['finalNetAmount'] = 0,
    public readonly salesTaxAmount: ITransaction['salesTaxAmount'] = 0,
    public readonly serviceChargeAmount: ITransaction['serviceChargeAmount'] = 0,
    public readonly tip: ITransaction['tip'] = 0,
    public readonly totalRefund: ITransaction['totalRefund'] = 0,
    public readonly grossAmount: ITransaction['grossAmount'] = 0,
    public readonly costOfGoods: ITransaction['costOfGoods'] = 0,
    public readonly discount: ITransaction['discount'] = null,
    public readonly transactionLevelDiscount: number = 0,
    public readonly salesTaxRate: ITransaction['salesTaxRate'] = 0,
    public readonly serviceChargeRate: ITransaction['serviceChargeRate'] = 0,
    public readonly status: ITransaction['status'] = TransactionStatus.PENDING,
    public readonly totalItems: ITransaction['totalItems'] = 0,
    public readonly store: ITransaction['store'] = new Store(),
    public registerId: ITransaction['registerId'] = 0,
    public readonly register: ITransaction['register'] = {name: ''},
    public readonly paymentMethod: ITransaction['paymentMethod'] = PaymentMethod.CASH,
    public readonly note: ITransaction['note'] = '',
    public readonly reason: ITransaction['reason'] = '',
    public readonly type: ITransaction['type'] = TransactionType.IN_STORE,
    public readonly createdAt: ITransaction['createdAt'] = new Date(),
    public readonly updatedAt: ITransaction['updatedAt'] = new Date(),
    public readonly salePrice: ITransaction['salePrice'],
    public readonly refundAmount: ITransaction['refundAmount'],
    public readonly discountAmount: ITransaction['discountAmount'],
    public readonly netSales: ITransaction['netSales'],
    public readonly taxAmount: ITransaction['taxAmount'],
    public readonly serviceAmount: ITransaction['serviceAmount'],
    public readonly grossSales: ITransaction['grossSales'],
    public readonly transactionItem: ITransaction['transactionItem'],
    public readonly totalDiscount: ITransaction['totalDiscount'] = 0,
    public readonly uuid: ITransaction['id'] = '',
    public readonly refundReason: ITransaction['refundReason'] = false,
    public readonly isRefund: ITransaction['isRefund'] = false,
  ) {}

  get salesTaxPercent() {
    return this.salesTaxRate * 100;
  }

  get serviceChargePercent() {
    return this.serviceChargeRate ? this.serviceChargeRate * 100 : null;
  }

  // TODO: Has same purpose as itemCount. Decide which to keep
  get quantity() {
    return this.item.reduce((acc, item) => acc + item.quantity, 0);
  }

  public static fromJSON(json: ITransaction) {
    return new Transaction(
      json.id,
      json.transactionTimestamp,
      json.item,
      json.staff,
      json.customer,
      json.finalNetAmount,
      json.salesTaxAmount,
      json.serviceChargeAmount,
      json.tip,
      json.totalRefund,
      json.grossAmount,
      json.costOfGoods,
      json.discount,
      json.transactionLevelDiscount,
      json.salesTaxRate,
      json.serviceChargeRate,
      json.status,
      json.totalItems,
      json.store,
      json.registerId,
      json.register,
      json.paymentMethod,
      json.note,
      json.reason,
      json.type,
      json.createdAt,
      json.updatedAt,
      json.salePrice,
      json.refundAmount,
      json.discountAmount,
      json.netSales,
      json.taxAmount,
      json.serviceAmount,
      json.grossSales,
      json.transactionItem,
      json.totalDiscount,
      json.uuid,
      json.refundReason,
      json.isRefund
    );
  }
}
